import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Image,
  Input,
  message,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../../api";
import DatePicker from "react-multi-date-picker";
import moment from "moment-jalaali";
import { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";

const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

function ProductIndex() {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [searchValue, setSearchValue] = useState(""); 
  const [searchData, setSearchData] = useState({});
  const formatter = new Intl.NumberFormat("fa-IR"); 

  useEffect(() => {
    setSearchData(Object.fromEntries(searchParams.entries()));
  }, [searchParams]);

  const fetchCategories = async () => {
    try {
      const response = await api.get("/panel/category", {
        params: { per_page: 10000, page: 1 },
      });
      setCategories(response.data.data || []);
    } catch (error) {
      console.error("خطا در دریافت دسته‌بندی‌ها:", error);
      message.error("دریافت دسته‌بندی‌ها با خطا مواجه شد");
    }
  };

  const fetchSellers = async () => {
    try {
      const response = await api.get("/panel/seller", {
        params: { per_page: 10000, page: 1 },
      });
      setSellers(response.data.data || []);
    } catch (error) {
      console.error("خطا در دریافت فروشنده‌ها:", error);
      message.error("دریافت فروشنده‌ها با خطا مواجه شد");
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchSellers();
  }, []);

  const handleFilterChange = (key, value) => {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      [key]: value || "",
      page: 1,
    });
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const page = searchParams.get("page") || 1;
      const pageSize = searchParams.get("per_page") || 20;
      const search = searchParams.get("search") || "";
      const category = searchParams.get("category") || null;
      const seller = searchParams.get("seller") || null;
      const has_body = searchParams.get("has_body") || null;
      const date = searchParams.get("date") || null;
      const has_order = searchParams.get("has_order") || null; 

      const response = await api.get("/panel/product?includes[]=variants", {
        params: {
          page,
          per_page: pageSize,
          search: search.trim(),
          category,
          seller,
          has_body,
          date,
          has_order, 
        },
      });

      setProducts(response.data.data);
      setPagination({
        current: parseInt(page),
        pageSize: parseInt(pageSize),
        total: response.data.meta.total,
      });
    } catch (error) {
      message.error("دریافت محصولات با خطا مواجه شد");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value); 
  };

  const handleSearch = () => {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      search: searchValue,
      page: 1,
    });
  };

  const handleTableChange = (pagination) => {
    const { current, pageSize } = pagination;
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      page: current,
      per_page: pageSize,
    });
  };

  useEffect(() => {
    fetchProducts();
  }, [searchParams]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/panel/product/${id}`);
      message.success("محصول با موفقیت حذف شد");
      fetchProducts();
    } catch (error) {
      message.error("خطا در حذف محصول");
    }
  };

  const handleDeleteVariant = async (variantId) => {
    try {
      await api.delete(`/panel/product-variant/${variantId}`);
      message.success("واریانت با موفقیت حذف شد");
      fetchProducts();
    } catch (error) {
      message.error("خطا در حذف واریانت");
    }
  };

  const variantColumns = [
    {
      title: "شناسه",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "عنوان",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "تصویر",
      dataIndex: "image",
      key: "image",
      render: (icon) => (
        <Image src={icon} alt="تصویر تنوع" width={100} preview={false} />
      ),
    },
    {
      title: "عملیات",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => {
              console.log(
                "Current Params:",
                Object.fromEntries(searchParams.entries())
              );

              navigate(`/productsVariation/edit/${record.id}`, {
                state: {
                  searchParams: Object.fromEntries(searchParams.entries()),
                },
              });
            }}
          />
          <Popconfirm
            title="آیا از حذف این واریانت اطمینان دارید؟"
            onConfirm={() => handleDeleteVariant(record.product_variant_id)}
            okText="بله"
            cancelText="خیر"
          >
            <Button type="primary" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const columns = [
    {
      title: "ترتیب",
      dataIndex: "order",
      key: "order",
    },
    {
      title: "شناسه",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "عنوان",
      dataIndex: "title",
      key: "title",
      render: (title, record) => (
        <>
          <div>{title}</div>
          {record.technical_code && (
            <Typography.Text type="secondary" style={{ fontSize: "12px", display: "block" }}>
              (کد فنی:{record.technical_code})
            </Typography.Text>
          )}
        </>
      ),
    },
    {
      title: "قیمت",
      dataIndex: "price",
      key: "price",
      render: (price) => (price ? formatter.format(price) : "۰"),
      filters: [
        { text: "دارای قیمت", value: "withPrice" },
        { text: "بدون قیمت", value: "withoutPrice" },
      ],
      onFilter: (value, record) => {
        if (value === "withPrice")
          return (
            record.price !== null &&
            record.price !== undefined &&
            record.price !== 0
          );
        if (value === "withoutPrice")
          return (
            record.price === null ||
            record.price === undefined ||
            record.price === 0
          );
      },
    },
    {
      title: "قیمت تخفیف",
      dataIndex: "offPrice",
      key: "offPrice",
    },
    {
      title: "دسته‌بندی‌ها",
      dataIndex: "category",
      key: "category",
      render: (category) => (
        <Space>
          {category?.map((category) => (
            <Tag key={category.id} color="blue">
              {category.name}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "تصویر",
      dataIndex: "image",
      key: "image",
      render: (icon) => (
        <Image src={icon} alt="تصویر محصول" width={100} preview={false} />
      ),
    },
    {
      title: "وضعیت",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "پیش نویس", value: 0 },
        { text: "منتشر شده", value: 1 },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => (status === 0 ? "پیش نویس" : "منتشر شده"),
    },
    {
      title: "تاریخ ایجاد",
      dataIndex: "created_at",
      key: "created_at",
    },
    {
      title: "عملیات",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate(`/productsVariation/add/${record.id}`)}
          >
            افزودن تنوع
          </Button>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => {
              const currentParams = Object.fromEntries(searchParams.entries());

              navigate(`/products/edit/${record.id}`, {
                state: { searchParams: currentParams },
              });
            }}
          />
          <Popconfirm
            title="آیا از حذف این محصول اطمینان دارید؟"
            onConfirm={() => handleDelete(record.id)}
            okText="بله"
            cancelText="خیر"
          >
            <Button type="primary" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleDateChange = (date) => {
    const formattedDate = moment(date?.format("YYYY/MM/DD")).format(
      "YYYY-MM-DD"
    );
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      date: formattedDate,
      page: 1,
    });
  };

  return (
    <Card>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">محصولات</h1>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/products/add")}
          >
            افزودن محصول
          </Button>
        </div>

        <div className="mb-4 flex gap-4">
          <Input
            placeholder="جستجوی محصولات..."
            onChange={handleSearchChange}
            value={searchValue} 
            className="max-w-md"
            allowClear
            addonAfter={
              <SearchOutlined
                onClick={handleSearch}
                style={{ cursor: "pointer" }}
              />
            }
          />
          <Select
            placeholder="ترتیب"
            onChange={(value) => handleFilterChange("has_order", value)}
            value={searchParams.get("has_order") || undefined}
            allowClear
            style={{ width: 200 }}
            options={[
              { value: "1", label: "دارای ترتیب" },
              { value: "0", label: "بدون ترتیب" },
            ]}
          />

<Select
  placeholder="انتخاب دسته‌بندی"
  onChange={(value) => handleFilterChange("category", value?.value || "")}
  value={
    searchParams.get("category")
      ? {
          value: searchParams.get("category"),
          label: categories.find(c => c.id == searchParams.get("category"))?.name || searchParams.get("category"),
        }
      : undefined
  }
  allowClear
  showSearch
  filterOption={(input, option) =>
    option.children.toLowerCase().includes(input.toLowerCase())
  }
  optionFilterProp="children"
  labelInValue
  style={{ width: 200 }}
>
  {categories.map((category) => (
    <Select.Option key={category.id} value={category.id}>
      {category.name}
    </Select.Option>
  ))}
</Select>

<Select
  placeholder="انتخاب فروشنده"
  onChange={(value) => handleFilterChange("seller", value?.value || "")}
  value={
    searchParams.get("seller")
      ? {
          value: searchParams.get("seller"),
          label: sellers.find(s => s.id == searchParams.get("seller"))?.name || searchParams.get("seller"),
        }
      : undefined
  }
  allowClear
  showSearch
  filterOption={(input, option) =>
    option.children.toLowerCase().includes(input.toLowerCase())
  }
  optionFilterProp="children"
  labelInValue
  style={{ width: 200 }}
>
  <Select.Option key="none" value="">
    فروشنده ندارد
  </Select.Option>
  {sellers.map((seller) => (
    <Select.Option key={seller.id} value={seller.id}>
      {seller.name}
    </Select.Option>
  ))}
</Select>

          <Select
            placeholder="وضعیت توضیحات"
            onChange={(value) => handleFilterChange("has_body", value)}
            value={
              searchParams.get("has_body") !== null
                ? String(searchParams.get("has_body"))
                : undefined
            }
            allowClear
            style={{ width: 200 }}
            options={[
              { value: "1", label: "دارای توضیحات" },
              { value: "0", label: "بدون توضیحات" },
            ]}
          />
          <DatePicker
            calendar={persian}
            locale={persian_fa}
            value={
              searchParams.get("date")
                ? new DateObject(
                    moment(searchParams.get("date"), "YYYY-MM-DD").toDate()
                  )
                : null
            }
            onChange={(date) => {
              if (!date) {
                // حذف فیلتر تاریخ
                const params = new URLSearchParams(searchParams);
                params.delete("date");
                setSearchParams(params);
              } else {
                const formattedDate = moment(date.toDate()).format(
                  "YYYY-MM-DD"
                );
                setSearchParams({
                  ...Object.fromEntries(searchParams.entries()),
                  date: formattedDate,
                });
              }
            }}
            format="YYYY/MM/DD"
            placeholder="انتخاب تاریخ"
          />
        </div>

        <Table
          columns={columns}
          dataSource={products}
          loading={loading}
          rowKey="id"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `مجموع ${total} مورد`,
          }}
          onChange={handleTableChange}
          scroll={{ x: true }}
          expandable={{
            expandedRowRender: (record) => {
              const variants = record.variants || [];
              return variants.length > 0 ? (
                <Table
                  columns={variantColumns}
                  dataSource={variants}
                  pagination={false}
                  rowKey="product_variant_id"
                  size="small"
                />
              ) : (
                <p style={{ padding: "16px", textAlign: "center" }}>
                  محصول تنوع ندارد
                </p>
              );
            },
            rowExpandable: (record) => (record.variants || []).length > 0,
          }}
        />
      </div>
    </Card>
  );
}

export default ProductIndex;
